<template>
  <section class="user-avatar-wrapper">
    <ion-avatar class="user-avatar-wrapper__avatar" :style="`width:${width}px;height:${height}px`">
      <img v-if="photo" :alt="fullName" :src="photo" />
      <ion-label v-else>{{ initialsName }}</ion-label>
    </ion-avatar>
    <div class="user-avatar-wrapper__upload" v-if="updatePhoto">
      <ion-icon color="white" :icon="camera" size="large" @click="uploadPhoto" />
    </div>
  </section>
  <input
    accept="image/jpeg, image/png, image/webp"
    ref="uploader"
    style="display: none"
    type="file"
    @change="onFileChanged"
  />
  <ion-modal :is-open="togglePhoto" :initial-breakpoint="0.15" :breakpoints="[0, 0.15]">
    <ion-content class="ion-padding profile-edit">
      <ion-item :button="true" @click="handleFileImport">
        <ion-icon :icon="folderOpenOutline" size="small" />
        <ion-label class="ion-margin-start text-xs" color="body"
          >{{ t('files.gallery') }}
        </ion-label>
      </ion-item>
      <ion-item :button="true" @click="takePicture">
        <ion-icon :icon="cameraOutline" size="small" />
        <ion-label class="ion-margin-start text-xs" color="body">Take a picture</ion-label>
      </ion-item>
    </ion-content>
  </ion-modal>
  <ion-modal :is-open="cropperPhoto" ref="modal" :initial-breakpoint="0.7" :breakpoints="[0, 0.7]">
    <section class="photo-wrapper__body">
      <div class="photo-wrapper__image">
        <h4 class="h4 text-align-left text-weight-bold py-xs">
          {{ t('profile.changePhotoProfile') }}
        </h4>
        <section class="photo-wrapper__cropper">
          <Cropper
            v-if="!loading"
            ref="cropper"
            class="photo-wrapper__background"
            :src="img"
            :auto-zoom="true"
            :stencil-props="{
              handlers: {},
              movable: true,
              resizable: true,
            }"
            :stencil-size="{
              width: cropperWidth,
              height: cropperHeight,
            }"
            @change="onChange"
          />
          <ion-skeleton-text v-else :animated="true" style="height: 420px; width: 100%">
          </ion-skeleton-text>
        </section>

        <ion-row>
          <ion-col size="6">
            <ion-button
              expand="block"
              color="primary"
              fill="clear"
              size="small"
              class="text-weight-bold"
              :disabled="loading"
              @click="cropperPhoto = false"
              >{{ t('actions.cancel') }}
            </ion-button>
          </ion-col>
          <ion-col size="6">
            <ion-button
              expand="block"
              color="primary"
              fill="solid"
              size="small"
              :disabled="loading"
              class="text-weight-bold"
              @click="savePhoto"
              >{{ loading ? t('actions.uploading') : t('actions.save') }}
            </ion-button>
          </ion-col>
        </ion-row>
      </div>
    </section>
  </ion-modal>
</template>
<script setup lang="ts">
  import {
    IonAvatar,
    IonLabel,
    IonIcon,
    IonContent,
    IonModal,
    IonItem,
    IonRow,
    IonCol,
    IonButton,
    IonSkeletonText,
  } from '@ionic/vue';
  import { useAuth } from '@/composables/useAuth';
  import { camera, cameraOutline, folderOpenOutline } from 'ionicons/icons';
  import { ref } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { Cropper, CropperResult } from 'vue-advanced-cropper';
  import { Camera, CameraResultType } from '@capacitor/camera';
  import { useNativePlatform } from '@/composables/useNativePlatform';
  import { useProfile } from '@/composables/useProfile';
  import {useToast} from "@/composables/useToast";
  import {resizeImage} from "@/utils/image-utils";

  const { isMobile } = useNativePlatform();

  const { fullName, photo, initialsName } = useAuth();
  const { handleUpdatePhoto } = useProfile();

  const isSelecting = ref<boolean>(false);
  const uploader = ref<HTMLInputElement>();
  const cropper = ref<CropperResult>();
  const togglePhoto = ref(false);
  const cropperPhoto = ref(false);
  const croppedPhotoBase64 = ref<string>();
  const cropperWidth = ref(300);
  const cropperHeight = ref(300);
  const fileName = ref('');
  const loading = ref<boolean>(false);
  const img = ref<string | ArrayBuffer | null | undefined>('');

  const { t } = useI18n();
  const { showToast } = useToast();
  const maxFileSize = 5 * 1024 * 1024;

  defineProps({
    width: {
      type: Number,
      required: false,
      default: 40,
    },
    height: {
      type: Number,
      required: false,
      default: 40,
    },
    updatePhoto: {
      type: Boolean,
      required: false,
      default: false,
    },
  });

  const uploadPhoto = async () => {
    togglePhoto.value = !togglePhoto.value;
  };

  const onChange = (canvas: CropperResult) => {
    croppedPhotoBase64.value = canvas.canvas?.toDataURL() ?? undefined;
  };

  const getBase64 = (file: File) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function () {
      cropperPhoto.value = true;

      setTimeout(() => {
        img.value = reader.result;
        loading.value = false;
      }, 1000);
    };
    reader.onerror = function (error) {
      console.log('Error: ', error);
    };
  };

  const onFileChanged = (e: any) => {
    isSelecting.value = false;
    loading.value = true;
    if (e.target.files && e.target.files[0]) {
      fileName.value = e.target.files[0].name;
      getBase64(e.target.files[0]);
    }
  };

  const handleFileImport = async () => {
    isSelecting.value = true;
    window.addEventListener(
      'focus',
      () => {
        isSelecting.value = false;
      },
      { once: true },
    );

    if (isMobile) {
      const permissions = await Camera.requestPermissions();
      if (permissions.photos === 'granted' || permissions.camera === 'granted') {
        uploader.value?.click();
      }
    }
    else uploader.value?.click();
  };

  const takePicture = async () => {
    if (isMobile) {
      const permissions = await Camera.requestPermissions();
      if (permissions.photos === 'granted' || permissions.camera === 'granted') {
        await Camera.getPhoto({
          quality: 50,
          allowEditing: false,
          resultType: CameraResultType.Base64,
        }).then(async (photo) => {
          if (photo?.base64String) {
            loading.value = true;
            fileName.value = 'photo.jpg';

            const parsed = await resizeImage(`data:image/jpeg;base64,${photo.base64String}`, 512);
            const imageContent = atob(parsed.replace('data:image/png;base64,', ''));
            const buffer = new ArrayBuffer(imageContent.length);
            const view = new Uint8Array(buffer);

            for (let n = 0; n < imageContent.length; n++) {
              view[n] = imageContent.charCodeAt(n);
            }
            const type = 'image/jpeg';
            const blob = new Blob([buffer], { type });

            const file = new File([blob], fileName.value, { lastModified: new Date().getTime(), type });
            if (file.size > maxFileSize) {
              showToast(t('validations.invalidFileSize', { size: '5MB' }), 'error');
              return;
            }
            getBase64(file)
          }
        });
      }
    }
  };

  const savePhoto = async () => {
    loading.value = true;
    handleUpdatePhoto(croppedPhotoBase64.value as string).then(() => {
      togglePhoto.value = false;
      cropperPhoto.value = false;
      loading.value = false;
    });
  };
</script>
<style scoped lang="scss">
  ion-icon {
    width: 25px;
    height: 25px;
  }

  ion-modal {
    --border-radius: 25px;
  }

  ion-icon {
    --color: var(--ion-color-body);
  }

  ion-content.profile-edit {
    --border-radius: 25px;
    --background: white;
  }

  ion-item {
    --background: white;
  }

  .photo-wrapper {
    &__body {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    }

    &__cropper {
      position: relative;

      &__loading {
        position: absolute;
        top: 50%;
        bottom: 50%;
        left: 50%;
        right: 50%;
      }
    }

    &__options {
      width: 100%;
    }

    &__image {
      margin-top: 20px;
      width: 90%;
    }

    &__background {
      background: #d4d9d9;
      height: 400px;
      width: 100%;
    }
  }

  .user-avatar-wrapper {
    position: relative;

    &__avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      background-color: var(--ion-color-primary-light);
      color: white;
      font-size: 1rem;
      font-weight: bold;
    }

    &__upload {
      position: absolute;
      bottom: 0;
      right: 0;
      width: 45px;
      height: 45px;
      background: var(--ion-color-primary);
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
    }
  }
</style>
